import { useState } from "react";

import { useMutationRequest } from "~/v2/api/hooks";
import { deleteBank } from "~/v2/api/rest/account";
import { deleteAchRelationship } from "~/v2/api/rest/banking";
import type { Relationship } from "../common/types";
import { UnlinkRelationshipModal } from "./UnlinkRelationshipModal";

export interface UseUnlinkRelationshipArgs {
  accountID: string;
  onSuccess: (relationship: Relationship) => void;
  onError: (error: Error, relationship: Relationship) => void;
}

export const useUnlinkRelationship = ({
  accountID,
  onSuccess,
  onError,
}: UseUnlinkRelationshipArgs) => {
  const [selectedRelationship, setSelectedRelationship] = useState<
    Relationship | undefined
  >(undefined);

  const removeRelationshipConnection = useMutationRequest(
    (relationship: Relationship) => {
      switch (relationship.type) {
        case "Wire":
          return deleteBank({
            bankID: relationship.id,
            accountID,
          });
        case "ACH":
          return deleteAchRelationship(accountID, relationship.id);
        default:
          throw new Error(
            "Relationship type invalid, can only unlink Wire and ACH connections.",
          );
      }
    },
    {
      onSuccess: (_data, relationship) => {
        setSelectedRelationship(undefined);
        onSuccess(relationship);
      },
      onError: (error, relationship) => onError(error, relationship),
    },
  );

  const unlinkRelationship = (relationship: Relationship) =>
    setSelectedRelationship(relationship);

  const onCancelUnlink = () => setSelectedRelationship(undefined);

  const onConfirmRemoveAccount = (relationship: Relationship) =>
    removeRelationshipConnection.mutate(relationship);

  const isLoading = removeRelationshipConnection.isPending;

  const unlinkModal = selectedRelationship && (
    <UnlinkRelationshipModal
      isOpen={!!selectedRelationship}
      relationship={selectedRelationship}
      onUnlink={onConfirmRemoveAccount}
      onCancel={onCancelUnlink}
      isLoading={isLoading}
    />
  );

  return {
    unlinkRelationship,
    isLoading,
    unlinkModal,
  };
};
